import { Controller } from "@hotwired/stimulus"
import { apiGet } from "../shared/api"
import { escapeHtml, loadTranslations, t } from "../shared/utils"

const STORAGE_KEY = "profiles_view"

export default class extends Controller {
  static targets = ["results", "pagination", "form", "count", "viewList", "viewCards"]
  static values = { apiUrl: String }

  connect() {
    loadTranslations()
    this.filterTimeout = null
    this.loading = false
    this.view = (typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "list"
    this._applyView()
    this._fetch(1)
  }

  disconnect() {
    if (this.filterTimeout) clearTimeout(this.filterTimeout)
  }

  onFilterChange() {
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => this._fetch(1), 200)
  }

  onSearchInput() {
    const val = (this.formTarget?.querySelector('[name="search"]')?.value || "").trim()
    if (val.length >= 3 || val.length === 0) {
      clearTimeout(this.filterTimeout)
      this.filterTimeout = setTimeout(() => this._fetch(1), 350)
    }
  }

  onSubmit(event) {
    event.preventDefault()
    this._fetch(1)
  }

  setViewList() {
    this.view = "list"
    this._applyView()
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, "list")
    if (this._lastData) this._renderProfiles(this._lastData, this._lastMeta)
  }

  setViewCards() {
    this.view = "cards"
    this._applyView()
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, "cards")
    if (this._lastData) this._renderProfiles(this._lastData, this._lastMeta)
  }

  _applyView() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.classList.remove("profiles-view-list", "profiles-view-cards")
    this.resultsTarget.classList.add(`profiles-view-${this.view}`)
    if (this.hasViewListTarget) this.viewListTarget.classList.toggle("active", this.view === "list")
    if (this.hasViewCardsTarget) this.viewCardsTarget.classList.toggle("active", this.view === "cards")
  }

  async _fetch(page) {
    if (this.loading) return
    this.loading = true

    if (this.hasResultsTarget) {
      this.resultsTarget.style.opacity = "0.5"
      this.resultsTarget.style.pointerEvents = "none"
    }

    try {
      const params = this._buildParams(page)
      const json = await apiGet(this.apiUrlValue, params)
      const { data, meta } = json

      this._lastData = data
      this._lastMeta = meta

      this._renderProfiles(data, meta)

      if (this.hasPaginationTarget) {
        this.paginationTarget.innerHTML = meta.total_pages > 1 ? this._renderPagination(meta, params) : ""
      }

      if (this.hasCountTarget) this.countTarget.textContent = String(meta.total_count)
    } catch (error) {
      console.error("Error fetching profiles:", error)
      if (this.hasResultsTarget) {
        this.resultsTarget.innerHTML = `<div class="col-12 text-center py-5 text-danger">${escapeHtml(t("common.error") || "Błąd")}</div>`
      }
    } finally {
      this.loading = false
      if (this.hasResultsTarget) {
        this.resultsTarget.style.opacity = ""
        this.resultsTarget.style.pointerEvents = ""
      }
    }
  }

  _buildParams(page) {
    const params = { page }
    if (!this.hasFormTarget) return params

    const formData = new FormData(this.formTarget)
    const search = (formData.get("search") || "").trim()
    if (search.length >= 3) params.search = search

    const voivodeship = formData.get("voivodeship")
    if (voivodeship) params.voivodeship = voivodeship

    const clubId = formData.get("club_id")
    if (clubId) params.club_id = clubId

    const ageCategoryIds = formData.getAll("age_category_ids[]").filter(Boolean)
    if (ageCategoryIds.length) params.age_category_ids = ageCategoryIds

    return params
  }

  _renderProfiles(profiles, meta) {
    if (!profiles.length) {
      this.resultsTarget.innerHTML = `
        <div class="col-12 text-center py-5">
          <i class="fa-solid fa-users fa-3x text-muted mb-3"></i>
          <h4>${escapeHtml(t("profiles.index.no_profiles"))}</h4>
          <p class="text-muted">${escapeHtml(t("profiles.index.no_profiles_hint"))}</p>
        </div>`
      return
    }

    if (this.view === "list") {
      this.resultsTarget.innerHTML = this._renderListView(profiles)
    } else {
      this.resultsTarget.innerHTML = `<div class="row g-4">${profiles.map(u => this._profileCard(u)).join("")}</div>`
    }
  }

  _renderListView(profiles) {
    const rows = profiles.map(u => this._profileRow(u))
    return `
      <div class="table-responsive">
        <table class="table table-sm table-hover align-middle">
          <tbody>${rows.join("")}</tbody>
        </table>
      </div>`
  }

  _profileRow(u) {
    const name = escapeHtml(u.full_name)
    const hasAvatar = u.avatar_url && !u.avatar_url.includes('defaults/avatar')
    const bg = u.sex === 1 ? '#e3f2fd' : '#fce4ec'
    const icon = u.sex === 1 ? 'fa-mars text-primary' : 'fa-venus text-danger'
    const avatarHtml = hasAvatar
      ? `<img src="${escapeHtml(u.avatar_url)}" alt="${name}" class="rounded-circle" style="width: 40px; height: 40px; object-fit: cover;">`
      : `<div class="d-inline-flex align-items-center justify-content-center rounded-circle" style="width: 40px; height: 40px; background: ${bg};"><i class="fa-solid ${icon}"></i></div>`

    let location = ""
    if (u.city) location = escapeHtml(u.city)
    if (u.voivodeship) {
      const v = t(`voivodeships.${u.voivodeship}`)
      location += (location ? ", " : "") + (v !== `voivodeships.${u.voivodeship}` ? v : u.voivodeship)
    }

    let classesHtml = ""
    if (u.dance_classes && (u.dance_classes.st || u.dance_classes.la)) {
      const parts = []
      if (u.dance_classes.st) parts.push(`ST: ${escapeHtml(u.dance_classes.st)}`)
      if (u.dance_classes.la) parts.push(`LA: ${escapeHtml(u.dance_classes.la)}`)
      classesHtml = `<span class="badge bg-dm-navy small">${parts.join(" / ")}</span>`
    }

    return `
      <tr>
        <td style="width: 50px;">${avatarHtml}</td>
        <td>
          <a href="/profiles/${escapeHtml(u.id)}" class="text-decoration-none fw-medium text-dark">${name}</a>
        </td>
        <td class="text-muted small">${location || "-"}</td>
        <td>${classesHtml}</td>
      </tr>`
  }

  _profileCard(u) {
    const name = escapeHtml(u.full_name)
    const hasAvatar = u.avatar_url && !u.avatar_url.includes('defaults/avatar')
    const bg = u.sex === 1 ? '#e3f2fd' : '#fce4ec'
    const icon = u.sex === 1 ? 'fa-mars text-primary' : 'fa-venus text-danger'
    const avatarHtml = hasAvatar
      ? `<img src="${escapeHtml(u.avatar_url)}" alt="${name}" class="rounded-circle" style="width: 70px; height: 70px; object-fit: cover;">`
      : `<div class="d-inline-flex align-items-center justify-content-center rounded-circle" style="width: 70px; height: 70px; background: ${bg};"><i class="fa-solid fa-2x ${icon}"></i></div>`

    const cityHtml = u.city
      ? `<p class="text-muted small mb-1"><i class="fa-solid fa-location-dot me-1"></i>${escapeHtml(u.city)}</p>`
      : ""

    let classesHtml = ""
    if (u.dance_classes && (u.dance_classes.st || u.dance_classes.la)) {
      classesHtml = `<div class="d-flex justify-content-center gap-1 mb-1">`
      if (u.dance_classes.st) classesHtml += `<span class="badge bg-dm-navy small">ST: ${escapeHtml(u.dance_classes.st)}</span>`
      if (u.dance_classes.la) classesHtml += `<span class="badge bg-dm-navy small">LA: ${escapeHtml(u.dance_classes.la)}</span>`
      classesHtml += `</div>`
    }

    return `
      <div class="col-lg-3 col-md-4 col-sm-6 col-12">
        <a href="/profiles/${u.id}" class="card shadow-sm h-100 text-center text-decoration-none text-dark d-block">
          <div class="card-body py-4">
            <div class="mb-3">${avatarHtml}</div>
            <h6 class="mb-1">${name}</h6>
            ${cityHtml}${classesHtml}
          </div>
        </a>
      </div>`
  }

  _renderPagination(meta, params) {
    const { current_page, total_pages } = meta
    const prev = current_page > 1 ? current_page - 1 : null
    const next = current_page < total_pages ? current_page + 1 : null

    let html = '<ul class="default-pagination lab-ul justify-content-center">'

    html += prev
      ? `<li><a href="#" data-action="click->profiles#goToPage" data-page="${prev}"><i class="fa-solid fa-angle-left"></i></a></li>`
      : '<li><span class="text-muted"><i class="fa-solid fa-angle-left"></i></span></li>'

    const start = Math.max(1, current_page - 2)
    const end = Math.min(total_pages, current_page + 2)

    for (let i = start; i <= end; i++) {
      const active = i === current_page ? ' class="active"' : ""
      html += `<li><a href="#" data-action="click->profiles#goToPage" data-page="${i}"${active}>${String(i).padStart(2, "0")}</a></li>`
    }

    html += next
      ? `<li><a href="#" data-action="click->profiles#goToPage" data-page="${next}"><i class="fa-solid fa-angle-right"></i></a></li>`
      : '<li><span class="text-muted"><i class="fa-solid fa-angle-right"></i></span></li>'

    html += "</ul>"
    return html
  }

  goToPage(event) {
    event.preventDefault()
    const page = event.currentTarget.dataset.page
    if (page) this._fetch(parseInt(page, 10))
  }
}
